export type Dir = "up" | "down" | "left" | "right";

export const DIRS: Record<Dir, { dx: number; dy: number }> = {
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
};

export enum Cell {
  Floor = 0,
  Wall = 1,
  Crystal = 2,
}

export type CrystalKind = "ball" | "diamond" | "diamond2" | "bounce1" | "bounce2" | "bomb" | "ice";
export type EnemyKind = "scorpion" | "spider" | "snake" | "bat" | "bouncer";
export type Phase = "start" | "playing" | "paused" | "levelclear" | "gameover" | "ending";
export const LETTERS = ["P", "U", "P", "P", "Y"] as const;

export interface LetterBubble {
  x: number;
  y: number;
  idx: number;
  ttl: number;
}

export interface Mover {
  x: number;
  y: number;
  tx: number;
  ty: number;
  t: number;
  dir: Dir;
}

export interface Player extends Mover {
  invuln: number;
  deadTimer: number;
}

export interface Enemy extends Mover {
  id: number;
  kind: EnemyKind;
  speed: number;
  wait: number;
  stuck: number;
  flying: boolean;
  frozen: number;
  flyTimer: number;
}

export interface Popup {
  x: number;
  y: number;
  text: string;
  life: number;
  max: number;
}

export interface Slider extends Mover {
  kind: CrystalKind;
  crushed: number;
  bounces: number;
}

export interface Flash {
  x: number;
  y: number;
  life: number;
  max: number;
  color: string;
  radius: number;
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: string;
  size: number;
  star?: boolean;
}

export type PowerKind = "freeze" | "ghost";

export interface PowerUp {
  x: number;
  y: number;
  kind: PowerKind;
  ttl: number;
}

export interface SpawnMarker {
  x: number;
  y: number;
  kind: EnemyKind;
  timer: number;
  max: number;
}

export interface GameState {
  cols: number;
  rows: number;
  grid: Cell[][];
  kinds: (CrystalKind | null)[][];
  player: Player;
  enemies: Enemy[];
  reserve: EnemyKind[];
  spawns: SpawnMarker[];
  cap: number;
  bonus: boolean;
  bonusResult: "win" | "fail" | null;
  exit: { x: number; y: number } | null;
  timeLeft: number;
  timeMax: number;
  sliders: Slider[];
  particles: Particle[];
  powerups: PowerUp[];
  popups: Popup[];
  flashes: Flash[];
  bombs: { x: number; y: number; timer: number }[];
  introHold: number;
  levelStartScore: number;
  bubbles: LetterBubble[];
  letters: boolean[];
  letterTimer: number;
  power: number;
  diamondsArmed: boolean;
  bonesUsed: boolean;
  daily: boolean;
  ballFloor: number;
  ballCap: number;
  retryAt: number;
  breather: boolean;
  wave: number;
  waveTimer: number;
  elapsed: number;
  fading: { x: number; y: number; t: number }[];
  freeze: number;
  ghost: number;
  spawnTimer: number;
  score: number;
  lives: number;
  level: number;
  phase: Phase;
  shake: number;
  lastCombo: number;
}

export interface Snapshot {
  phase: Phase;
  score: number;
  lives: number;
  level: number;
  best: number;
  enemiesLeft: number;
  letters: boolean[];
  god: boolean;
  bonus: boolean;
  bonusResult: "win" | "fail" | null;
  timedOut: boolean;
  retryAt: number;
  daily: boolean;
  wave: number;
  elapsed: number;
  breather: boolean;
  introHold: number;
}

export type GameEvent =
  | "push"
  | "wave"
  | "combo"
  | "shatter"
  | "crush"
  | "death"
  | "levelclear"
  | "gameover"
  | "ending"
  | "spawn"
  | "tunnel"
  | "exit"
  | "tick"
  | "timeout"
  | "powerup"
  | "eat"
  | "letter"
  | "extra"
  | "power"
  | "roll"
  | "thud"
  | "merge"
  | "explode"
  | "fuse"
  | "freezeburst"
  | "crack"
  | "start";
